import { Component, computed, effect, inject, signal, Signal, untracked } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { RouterOutlet } from '@angular/router';
import { HubConnectionState } from '@microsoft/signalr';
import { distinctUntilChanged, map, of, switchMap, timer } from 'rxjs';
import { ClientService } from '@client/work-packages/services/client.ws.service';
import { UploadService } from '@client/upload/services/upload.ws.service';
import { ConnectivityModal } from '@client/shared/components/connectivity-modal/connectivity-modal';
import { BastionKeyGate } from '@client/shared/components/bastion-key-gate/bastion-key-gate';
import { SshTunnelStatus } from '@client/shared/interfaces/tunnel-health.interface';
import { PnToast } from '@privanot/shared-ui/primeng-acl/toast';
import { UploadProgressService } from '@client/shared/services/upload-progress.service';
import { EntraAuthService } from '@client/auth/services/entra-auth.service';

const SHOW_CONNECTING_DELAY_MS = 500;

type ConnectionFailureKind = 'connection' | 'unauthorized';

@Component({
    selector: 'app-root',
    imports: [RouterOutlet, ConnectivityModal, BastionKeyGate, PnToast],
    templateUrl: './app.html',
    styleUrl: './app.scss',
})
export class App {
    private clientWSService = inject(ClientService);
    private uploadService = inject(UploadService);
    private uploadProgress = inject(UploadProgressService);
    private auth = inject(EntraAuthService);

    public readonly isSplashWindow = !!window.connectivityCheck;

    /** Signed in, or Entra disabled. Until then only the sign-in page shows: no hubs, no connectivity modal. */
    public readonly authReady = this.auth.isReady;
    private connectStarted = false;

    public isConnecting: Signal<boolean> = toSignal(
        toObservable(this.clientWSService.state).pipe(
            map((state) => state !== HubConnectionState.Connected),
            distinctUntilChanged(),
            switchMap((disconnected) => (disconnected ? timer(SHOW_CONNECTING_DELAY_MS).pipe(map(() => true)) : of(false))),
        ),
        { initialValue: false },
    );

    public connectionError = signal<ConnectionFailureKind | null>(null);
    public isRetrying = signal<boolean>(false);
    private readonly tunnelReasonKey = signal<string | null>(null);

    public hasError = computed(() => this.connectionError() !== null);
    public errorMessageKey = computed(() => {
        const reasonKey = this.tunnelReasonKey();
        if (reasonKey) {
            return reasonKey;
        }
        return this.connectionError() === 'unauthorized' ? 'splash.unauthorized' : 'splash.error';
    });

    public readonly bastionState = this.clientWSService.bastionEnrollmentState;
    public readonly bastionKeyBlocked = computed(() => {
        const state = this.bastionState();
        // 'unknown' means the key is configured but Privanot couldn't report its status — don't block a working
        // app on a failed status check; it surfaces in the sidebar's secure-connection banner instead. 'in_flight'
        // is a transient enrolling state and never gates.
        return !this.isSplashWindow && state !== null && state !== 'active' && state !== 'unknown' && state !== 'in_flight';
    });

    private bastionStateUnsubscribe?: () => void;

    public recheckKey = async (): Promise<void> => {
        const state = await this.clientWSService.refreshBastionEnrollmentState();
        if (state !== 'active') {
            // Negative result — keep the button disabled a moment longer so it can't be spammed.
            await new Promise((resolve) => setTimeout(resolve, 1000));
        }
    };

    constructor() {
        // The splash window runs its own connection checks; the main window connects once auth is settled.
        effect(() => {
            if (this.isSplashWindow || !this.authReady() || this.connectStarted) {
                return;
            }
            this.connectStarted = true;
            untracked(() => void this.connect());
        });
    }

    public retry = async (): Promise<void> => {
        this.isRetrying.set(true);
        this.connectionError.set(null);
        try {
            await this.connect();
        } finally {
            this.isRetrying.set(false);
        }
    };

    private async connect(): Promise<void> {
        this.tunnelReasonKey.set(null);

        try {
            await this.clientWSService.listen();
        } catch {
            this.connectionError.set('connection');
            return;
        }

        try {
            await this.clientWSService.ensureClientDataLoaded();
        } catch {
            await this.applyTunnelFailureReason();
            this.connectionError.set('unauthorized');
            return;
        }

        try {
            await this.clientWSService.refreshBastionEnrollmentState();
        } catch {
            // best-effort — the gate stays hidden until a state is known
        }
        this.bastionStateUnsubscribe ??= this.clientWSService.onBastionEnrollmentState(() => {});

        try {
            await this.uploadService.listen();
            try {
                const active = await this.uploadService.getActiveUploadBatch();
                if (active) {
                    await this.uploadProgress.resumeIfActive(active);
                }
            } finally {
                await this.uploadService.stopListening();
            }
        } catch {
            // resume is best-effort — don't block app boot
        }
    }

    private async applyTunnelFailureReason(): Promise<void> {
        try {
            const health = await this.clientWSService.getTunnelHealth();
            if (health.status === SshTunnelStatus.FAILED && health.failureReason !== 'none') {
                this.tunnelReasonKey.set(`tunnel.reason.${health.failureReason}`);
            }
        } catch {
            // best-effort — fall back to the generic message
        }
    }
}
